import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SyntheseModules = ({ synthesis, sortedPlanning, dossierId, token }) => {
  const [synthBackend, setSynthBackend] = useState(null);

  useEffect(() => {
    if (!dossierId) return;
    axios.get(`${API_URL}/dossiers/${dossierId}/synthese-modules`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setSynthBackend(res.data)).catch(() => {});
  }, [dossierId, token]);

  const merged = synthesis.map(s => {
    const be = synthBackend?.find(b => b.module_id === s.module_id);
    return { ...s, duree_realisee: be?.duree_realisee || 0, confirme: be?.confirme || false };
  });
  if (synthBackend) {
    for (const be of synthBackend) {
      if (!merged.find(m => m.module_id === be.module_id)) {
        merged.push({ module_id: be.module_id, code: be.code, titre: be.titre, duree_prevue: be.duree_prevue || 0, duree_planifiee: be.duree_planifiee || 0, jours: be.jours_planifies || 0, inFormation: be.in_formation || false, duree_realisee: be.duree_realisee || 0, confirme: be.confirme || false });
      }
    }
  }

  return (
    <div className="border rounded-lg overflow-hidden" data-testid="modules-synthesis">
      <div className="bg-slate-50 px-4 py-2 border-b flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-700">Synthèse des modules</h4>
        <div className="text-xs text-slate-500">{sortedPlanning.length} jours · {sortedPlanning.length * 7}h prévues</div>
      </div>
      <div className="divide-y">
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] gap-0 bg-slate-100 px-4 py-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-wide">
          <span>Module</span><span className="text-center">Durée prévue</span><span className="text-center">Planifié</span><span className="text-center">Réalisé</span><span className="text-center">Restant</span><span className="text-center">Statut</span>
        </div>
        {merged.map(s => {
          const remaining = s.duree_prevue - s.duree_planifiee;
          const isOver = remaining < 0;
          const isDone = remaining === 0 && s.duree_prevue > 0;
          const isRealDone = s.duree_realisee >= s.duree_prevue && s.duree_prevue > 0;
          const isExtra = !s.inFormation;
          const datesModule = sortedPlanning.filter(d => d.type_journee !== 'stage' && (d.modules_pedagogiques || []).some(mp => mp.module_id === s.module_id)).map(d => d.date);
          return (
            <div key={s.module_id} className={`grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] gap-0 items-center px-4 py-2 ${isExtra ? 'bg-orange-50/50' : ''}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-mono text-violet-600">{s.code}</span>
                  <span className="text-xs text-slate-700 truncate">{s.titre}</span>
                  {isExtra && <span className="text-[9px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-600 whitespace-nowrap">Hors formation</span>}
                  {s.confirme && <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-600">Confirmé</span>}
                </div>
                {datesModule.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1" data-testid={`dates-module-${s.module_id}`}>
                    {datesModule.map(dt => <span key={dt} className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 whitespace-nowrap">{format(parseISO(dt), 'EEE d MMM', { locale: fr })}</span>)}
                  </div>
                )}
              </div>
              <span className="text-xs text-center text-slate-600">{s.duree_prevue > 0 ? `${s.duree_prevue}h` : '-'}</span>
              <span className="text-xs text-center font-medium text-slate-800">{s.duree_planifiee > 0 ? `${s.duree_planifiee}h` : '-'}</span>
              <span className={`text-xs text-center font-medium ${s.duree_realisee > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>{s.duree_realisee > 0 ? `${s.duree_realisee}h` : '-'}</span>
              <span className={`text-xs text-center font-medium ${isOver ? 'text-orange-600' : isDone ? 'text-emerald-600' : remaining > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                {s.duree_prevue > 0 ? (isRealDone || isDone ? '0h' : `${Math.abs(s.duree_prevue - Math.max(s.duree_planifiee, s.duree_realisee))}h`) : '-'}
              </span>
              <div className="flex justify-center">
                {isRealDone && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Réalisé</span>}
                {!isRealDone && isDone && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Planifié</span>}
                {!isDone && !isRealDone && remaining > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Partiel</span>}
                {isOver && !isRealDone && <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">Dépassé</span>}
                {s.duree_prevue === 0 && s.duree_planifiee === 0 && s.duree_realisee === 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Non planifié</span>}
                {isExtra && (s.duree_planifiee > 0 || s.duree_realisee > 0) && <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-600">Ajouté</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
